import { Link } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from "recharts";
import { Loader2, ShoppingBag, Package, GraduationCap, Users, Wallet, TrendingUp, DollarSign, BadgePercent, Trophy, KeyRound } from "lucide-react";
import { useVendorDashboard } from "@/hooks/useVendor";
import { formatPrice, formatDate } from "@/lib/format";
import { fileUrl } from "@/lib/api";

const ORDER_LABEL = {
  pending: "En attente", payment_review: "Paiement à vérifier", payment_rejected: "Paiement rejeté",
  processing: "En traitement", preparing: "En préparation", shipped: "Expédiée",
  delivered: "Livrée", completed: "Terminée", cancelled: "Annulée", refunded: "Remboursée",
};
const STATUS_COLORS = ["#0052FF", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#10B981", "#64748B", "#F97316", "#EC4899", "#84CC16"];
const LIC_LABEL = { available: "Disponibles", reserved: "Réservées", sold: "Vendues", cancelled: "Annulées" };

const STAT_CARDS = [
  { key: "revenue", label: "Chiffre d'affaires", icon: DollarSign, money: true, tone: "text-emerald-600" },
  { key: "orders_count", label: "Commandes", icon: ShoppingBag, tone: "text-brand" },
  { key: "products_count", label: "Produits", icon: Package, tone: "text-indigo-600" },
  { key: "formations_count", label: "Formations", icon: GraduationCap, tone: "text-purple-600" },
  { key: "clients_count", label: "Clients", icon: Users, tone: "text-sky-600" },
  { key: "units_sold", label: "Produits vendus", icon: TrendingUp, tone: "text-amber-600" },
  { key: "month_sales", label: "Ventes du mois", icon: BadgePercent, money: true, tone: "text-rose-600" },
  { key: "net_revenue", label: "Revenus disponibles", icon: Wallet, money: true, tone: "text-emerald-700" },
];

export default function VendorDashboard() {
  const { data, isLoading } = useVendorDashboard();
  if (isLoading || !data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>;

  const pie = [
    { name: "Produits", value: data.published_products || 0, color: "#0052FF" },
    { name: "Formations", value: data.formations_count || 0, color: "#7C3AED" },
    { name: "Ventes", value: data.units_sold || 0, color: "#F59E0B" },
  ];

  return (
    <div className="space-y-6" data-testid="vendor-dashboard">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-ink">Bonjour 👋</h1>
        <p className="mt-1 text-sm text-slate-500">Voici un aperçu de votre activité aujourd'hui.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {STAT_CARDS.map(({ key, label, icon: Icon, money, tone }) => (
          <div key={key} data-testid={`vstat-${key}`} className="rounded-2xl border border-line bg-white p-4 transition-transform duration-200 hover:-translate-y-0.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</span>
              <Icon className={`h-4 w-4 ${tone}`} />
            </div>
            <p className="mt-2 font-display text-xl font-extrabold text-ink">
              {money ? formatPrice(data[key] || 0) : (data[key] || 0)}
            </p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <div className="rounded-2xl border border-line bg-white p-5">
          <p className="font-display text-base font-bold text-ink">Ventes des 30 derniers jours</p>
          <div className="mt-4 h-64 min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
              <AreaChart data={data.chart}>
                <defs>
                  <linearGradient id="vg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0052FF" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0052FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={6} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" width={40} />
                <Tooltip formatter={(v) => formatPrice(v)} labelFormatter={(d) => formatDate(d)} />
                <Area type="monotone" dataKey="total" stroke="#0052FF" strokeWidth={2.5} fill="url(#vg)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-line bg-white p-5">
          <p className="font-display text-base font-bold text-ink">Répartition</p>
          <div className="mt-2 h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" innerRadius={45} outerRadius={70} paddingAngle={3}>
                  {pie.map((e) => <Cell key={e.name} fill={e.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-1.5">
            {pie.map((e) => (
              <div key={e.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-slate-600"><span className="h-2.5 w-2.5 rounded-full" style={{ background: e.color }} />{e.name}</span>
                <span className="font-bold text-ink">{e.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Meilleures ventes */}
        <div className="rounded-2xl border border-line bg-white p-5" data-testid="vendor-best-sellers">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-500" />
            <p className="font-display text-base font-bold text-ink">Meilleures ventes</p>
          </div>
          {data.best_sellers?.length ? (
            <div className="mt-3 space-y-2.5">
              {data.best_sellers.map((p, i) => (
                <div key={p.id} className="flex items-center gap-3" data-testid={`best-seller-${p.id}`}>
                  <span className="w-4 font-display text-sm font-black text-slate-300">{i + 1}</span>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
                    {p.image_url ? <img src={fileUrl(p.image_url)} alt="" className="h-full w-full object-cover" /> : <Package className="h-4 w-4 text-slate-300" />}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs font-bold text-ink">{p.name}</span>
                  <span className="text-xs font-black text-brand">{p.sales} vente{p.sales > 1 ? "s" : ""}</span>
                </div>
              ))}
            </div>
          ) : <p className="py-8 text-center text-xs text-slate-400">Aucune vente enregistrée.</p>}
        </div>

        {/* État des licences */}
        <div className="rounded-2xl border border-line bg-white p-5" data-testid="vendor-license-status">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-brand" />
            <p className="font-display text-base font-bold text-ink">État des licences</p>
          </div>
          <div className="mt-4 space-y-3">
            {Object.entries(LIC_LABEL).map(([k, label]) => {
              const total = Object.values(data.license_status || {}).reduce((a, b) => a + b, 0);
              const v = (data.license_status || {})[k] || 0;
              const pct = total ? Math.round((v / total) * 100) : 0;
              return (
                <div key={k}>
                  <div className="flex justify-between text-xs">
                    <span className="font-semibold text-slate-600">{label}</span>
                    <span className="font-black text-ink">{v}</span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-brand transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <Link to="/vendeur/licences" className="mt-4 inline-block text-xs font-bold text-brand hover:underline" data-testid="vendor-goto-licenses">Gérer mes licences →</Link>
        </div>

        {/* Répartition par statut */}
        <div className="rounded-2xl border border-line bg-white p-5" data-testid="vendor-orders-status">
          <p className="font-display text-base font-bold text-ink">Commandes par statut</p>
          {data.orders_by_status?.length ? (
            <>
              <div className="mt-2 h-36">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.orders_by_status} dataKey="count" nameKey="status" innerRadius={32} outerRadius={55} paddingAngle={3}>
                      {data.orders_by_status.map((e, i) => <Cell key={e.status} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, ORDER_LABEL[n] || n]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 space-y-1">
                {data.orders_by_status.map((e, i) => (
                  <div key={e.status} className="flex items-center justify-between text-[11px]">
                    <span className="flex items-center gap-1.5 text-slate-600">
                      <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLORS[i % STATUS_COLORS.length] }} />
                      {ORDER_LABEL[e.status] || e.status}
                    </span>
                    <span className="font-bold text-ink">{e.count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="py-8 text-center text-xs text-slate-400">Aucune commande.</p>}
        </div>
      </div>

      <div className="rounded-2xl border border-line bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <p className="font-display text-base font-bold text-ink">Commandes récentes</p>
          <Link to="/vendeur/commandes" className="text-xs font-bold text-brand hover:underline" data-testid="vendor-see-orders">Voir tout</Link>
        </div>
        {data.recent_orders?.length ? (
          <div className="divide-y divide-line">
            {data.recent_orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between py-2.5 text-sm">
                <div>
                  <p className="font-bold text-ink">{o.order_number}</p>
                  <p className="text-xs text-slate-500">{o.customer} · {formatDate(o.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-ink">{formatPrice(o.total)}</p>
                  <span className={`text-[11px] font-bold ${o.payment_status === "paid" ? "text-emerald-600" : "text-amber-600"}`}>
                    {o.payment_status === "paid" ? "Payé" : "En attente"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="py-6 text-center text-sm text-slate-400">Aucune commande pour le moment.</p>}
      </div>
    </div>
  );
}
